import { Cart, CartTotalResponse } from '@diamond/shared/types';
import {
  areCartProductsEqual,
  areVoucherCodesEqual,
  CART_TOTAL_QUERY_KEY,
  normalizeCartTotalPayload,
  selectCartProductsForTotals,
  useCartStore,
  useShipToStore,
  useVoucherStore,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useDebounce } from 'usehooks-ts';
import shallow from 'zustand/shallow';

import {
  extractVoucherBannerData,
  VoucherBannerData,
} from '../banners/voucher-banner-data';

export const useVoucherBannerData = (
  selectedIds: Set<string>,
  cartItems?: Cart[]
): VoucherBannerData[] => {
  const warehouse = useShipToStore((state) => state.selectedShipToId);
  const selectedProducts = useCartStore(
    selectCartProductsForTotals,
    areCartProductsEqual
  );
  const appliedVouchers = useVoucherStore(
    (state) => state.appliedVouchers,
    areVoucherCodesEqual
  );
  // Extract codes from voucher objects for API payload
  const voucherCodes = useMemo(
    () => appliedVouchers.map((v) => v.code),
    [appliedVouchers]
  );
  const queryClient = useQueryClient();

  const cartItemsRef = useRef(cartItems);
  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  const payload = useMemo(
    () => ({
      warehouse_id: warehouse,
      is_return_promos: true,
      products: selectedProducts,
      voucher_codes: voucherCodes.length > 0 ? voucherCodes : undefined,
    }),
    [warehouse, selectedProducts, voucherCodes]
  );
  const debouncedPayload = useDebounce(payload, 500);
  const normalizedPayload = useMemo(
    () => normalizeCartTotalPayload(debouncedPayload),
    [debouncedPayload]
  );
  const queryKey = [CART_TOTAL_QUERY_KEY, normalizedPayload];
  const cartTotalData = queryClient.getQueryData<CartTotalResponse>(
    queryKey as unknown as [string, unknown]
  );

  const voucherBanners = useMemo(() => {
    if (selectedIds.size === 0 || !cartTotalData?.vouchers?.applied_vouchers) {
      return [];
    }

    return cartTotalData.vouchers.applied_vouchers
      .map((voucher) => extractVoucherBannerData(voucher, cartItemsRef.current))
      .filter(
        (banner): banner is VoucherBannerData =>
          banner !== null && banner.type === 'free-item'
      );
  }, [cartTotalData, selectedIds.size]);

  return voucherBanners;
};
